import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { FiCoffee, FiSearch } from "react-icons/fi";
import ModalHeader from "./ModalHeader";
import ModalStats from "./ModalStats";
import MacrosSummary from "./MacrosSummary";
import FoodItemsFilters from "./FoodItemsFilters";
import FoodItemsList from "./FoodItemsList";
import { MealTypeDetails } from "../types";

interface MealTypeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  mealType: {
    meal_type: string;
    meal_name: string;
  };
  details: MealTypeDetails | null;
  loading: boolean;
}

export default function MealTypeDetailsModal({
  isOpen,
  onClose,
  mealType,
  details,
  loading,
}: MealTypeDetailsModalProps) {
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [itemSortBy, setItemSortBy] = useState<
    "occurrences" | "calories" | "name" | "protein" | "carbs" | "fat"
  >("occurrences");

  // Filtrar e ordenar itens
  const filteredAndSortedItems = useMemo(() => {
    if (!details?.items) return [];

    let filtered = details.items.filter((item) =>
      item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()),
    );

    // Ordenar
    filtered = [...filtered].sort((a, b) => {
      switch (itemSortBy) {
        case "occurrences":
          return b.occurrences - a.occurrences;
        case "calories":
          return b.total_calories - a.total_calories;
        case "protein":
          return b.macros.proteina_g - a.macros.proteina_g;
        case "carbs":
          return b.macros.carboidrato_g - a.macros.carboidrato_g;
        case "fat":
          return b.macros.lipideos_g - a.macros.lipideos_g;
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [details?.items, itemSearchTerm, itemSortBy]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999999] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4 backdrop-blur-sm">
      <div className="relative flex h-[90vh] sm:h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark">
        <ModalHeader mealName={mealType.meal_name} onClose={onClose} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-4 sm:p-8 dark:bg-gray-900/20">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-20">
              <div className="relative">
                <div className="h-12 w-12 sm:h-16 sm:w-16 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600 dark:border-purple-800 dark:border-t-purple-400"></div>
              </div>
              <p className="mt-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Carregando detalhes...
              </p>
            </div>
          ) : details && details.items.length > 0 ? (
            <>
              <ModalStats
                totalItems={details.total_items}
                totalOccurrences={details.total_occurrences}
                totalCalories={details.items.reduce(
                  (sum, item) => sum + item.total_calories,
                  0,
                )}
              />

              <MacrosSummary macros={details.macros_totais} />

              {details.items.length > 0 && (
                <FoodItemsFilters
                  searchTerm={itemSearchTerm}
                  onSearchChange={setItemSearchTerm}
                  sortBy={itemSortBy}
                  onSortChange={setItemSortBy}
                />
              )}

              {filteredAndSortedItems.length > 0 ? (
                <FoodItemsList items={filteredAndSortedItems} />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
                  <div className="rounded-full bg-gradient-to-br from-gray-100 to-gray-100 p-4 sm:p-5 dark:from-gray-800/30 dark:to-gray-800/30">
                    <FiSearch
                      size={32}
                      className="sm:w-10 sm:h-10 text-gray-400 dark:text-gray-600"
                    />
                  </div>
                  <p className="mt-3 sm:mt-4 text-sm sm:text-base font-semibold text-gray-700 dark:text-gray-300 text-center">
                    Nenhum alimento encontrado
                  </p>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
                    Tente ajustar os filtros ou buscar por outro termo
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
              <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-5 sm:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
                <FiCoffee
                  size={40}
                  className="sm:w-12 sm:h-12 text-gray-400 dark:text-gray-600"
                />
              </div>
              <p className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 text-center">
                Nenhum alimento encontrado
              </p>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
                Não há alimentos registrados para este tipo de refeição no
                período selecionado
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
